import React, { useEffect, useState } from 'react'
import { useTheme } from '../../hooks/useTheme'

interface CountdownTimerProps {
  expiresAt: string // ISO 8601 timestamp
  onExpire?: () => void
  className?: string
  warningThreshold?: number // Segundos para mostrar warning (default: 60)
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  expiresAt,
  onExpire,
  className = '',
  warningThreshold = 60
}) => {
  const { colors } = useTheme()
  const [timeLeft, setTimeLeft] = useState<number>(0)
  const [isExpired, setIsExpired] = useState(false)
  const hasCalledExpire = React.useRef(false) // Track si ya se llamó onExpire

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime()
      const expiryTime = new Date(expiresAt).getTime()
      const difference = expiryTime - now
      const seconds = Math.floor(difference / 1000)

      // ✅ FIX: Comparar segundos, no milisegundos
      if (seconds <= 0) {
        setTimeLeft(0)
        setIsExpired(true)

        // Llamar onExpire solo una vez
        if (onExpire && !hasCalledExpire.current) {
          hasCalledExpire.current = true
          onExpire()
        }
        return 0
      }

      return seconds
    }

    // Calcular tiempo inicial
    const initial = calculateTimeLeft()
    setTimeLeft(initial)

    // Actualizar cada segundo
    const timer = setInterval(() => {
      const remaining = calculateTimeLeft()
      setTimeLeft(remaining)

      if (remaining <= 0) {
        clearInterval(timer)
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [expiresAt, onExpire]) // ✅ Removido isExpired de dependencias

  // Formatear tiempo
  const minutes = Math.floor(timeLeft / 60)
  const seconds = timeLeft % 60

  // Determinar color según tiempo restante
  const getColorClasses = () => {
    if (isExpired) {
      return 'bg-red-100 border-red-300 text-red-800'
    }
    if (timeLeft <= warningThreshold) {
      return 'bg-yellow-100 border-yellow-300 text-yellow-800'
    }
    return 'bg-green-100 border-green-300 text-green-800'
  }

  // Obtener icono según estado
  const getIcon = () => {
    if (isExpired) {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }

  return (
    <div className={`${getColorClasses()} border-2 rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {getIcon()}
          <div>
            <p className="text-sm font-semibold">
              {isExpired ? '⚠️ Tiempo expirado' : 'Tiempo restante'}
            </p>
            {!isExpired && (
              <p className="text-xs opacity-75">
                Completa tu reserva antes de que expire
              </p>
            )}
          </div>
        </div>

        <div className="text-right">
          {isExpired ? (
            <p className="text-2xl font-bold">00:00</p>
          ) : (
            <p className="text-2xl font-bold tabular-nums">
              {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
            </p>
          )}
        </div>
      </div>

      {/* Barra de progreso */}
      {!isExpired && (
        <div className="mt-3 bg-white/50 rounded-full h-2 overflow-hidden">
          <div
            style={{
              backgroundColor: timeLeft <= warningThreshold ? colors.warning : colors.success,
              width: `${(timeLeft / (5 * 60)) * 100}%` // Asumiendo 5 minutos max
            }}
            className="h-full transition-all duration-1000"
          />
        </div>
      )}
    </div>
  )
}

export default CountdownTimer
